import { useCallback, useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useNotifications } from '../../../store/useStore';
import { listeService } from '../../../lib/email/listeService';
import type { EmailLista } from '../../../types/email.types';

interface ListeDelContattoProps {
  contattoId: string;
  liste: EmailLista[];
  /** Avvisa la pagina che i numeri delle liste sono cambiati. */
  onCambiato: () => void;
}

/** Nella scheda del contatto: in quali liste e', e metterlo o toglierlo. */
export function ListeDelContatto({ contattoId, liste, onCambiato }: ListeDelContattoProps) {
  const { addNotification } = useNotifications();
  const [sue, setSue] = useState<string[]>([]);
  const [lavorando, setLavorando] = useState(true);

  useEffect(() => {
    let annullato = false;
    setLavorando(true);
    listeService
      .delContatto(contattoId)
      .then((ids) => !annullato && setSue(ids))
      .catch(() => !annullato && setSue([]))
      .finally(() => !annullato && setLavorando(false));
    return () => {
      annullato = true;
    };
  }, [contattoId]);

  const cambia = useCallback(
    async (listaId: string, metti: boolean) => {
      setLavorando(true);
      try {
        if (metti) await listeService.aggiungi(listaId, [contattoId]);
        else await listeService.togli(listaId, [contattoId]);
        setSue((prima) => (metti ? [...prima, listaId] : prima.filter((id) => id !== listaId)));
        onCambiato();
      } catch (errore) {
        addNotification({
          type: 'error',
          title: metti ? 'Non riesco a metterlo nella lista' : 'Non riesco a toglierlo dalla lista',
          message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
        });
      } finally {
        setLavorando(false);
      }
    },
    [contattoId, onCambiato, addNotification]
  );

  const nome = (id: string) => liste.find((l) => l.id === id)?.name ?? 'lista';
  const disponibili = liste.filter((l) => !sue.includes(l.id));

  return (
    <div>
      <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 flex items-center gap-2">
        Liste
        {lavorando && <Loader2 className="w-3 h-3 animate-spin" />}
      </p>

      <div className="flex flex-wrap items-center gap-1.5">
        {sue.length === 0 && !lavorando && (
          <span className="text-xs text-neutral-400">Non e' in nessuna lista.</span>
        )}
        {sue.map((id) => (
          <span
            key={id}
            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] font-medium bg-primary-50 text-primary-700 border border-primary-200"
          >
            {nome(id)}
            <button
              type="button"
              onClick={() => cambia(id, false)}
              disabled={lavorando}
              className="rounded-full p-0.5 hover:bg-primary-100"
              aria-label={`Togli da ${nome(id)}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}

        {disponibili.length > 0 && (
          <select
            value=""
            disabled={lavorando}
            onChange={(e) => e.target.value && cambia(e.target.value, true)}
            className="h-7 rounded-full border border-dashed border-gray-300 bg-white px-2 text-[11px] text-neutral-600"
            aria-label="Metti in una lista"
          >
            <option value="">+ Metti in una lista</option>
            {disponibili.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}
